import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateTime } from 'luxon';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { MatEndDate, MatStartDate } from '@angular/material/datepicker';
import { CONFIG } from '../../../config';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MultiFormatLuxonDateAdapter } from '../../../core/services/multi-format-luxon-date-adapter';

const DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: [
      'M/d/yy',
      'M-d-yy'
    ]
  },
  display: {
    dateInput: 'M/d/yyyy',
    // `LLL` so the calendar's period button reads "SEP 2026" rather than "09 2026" (RA-2738).
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'DDDD',
    monthYearA11yLabel: 'LLLL yyyy'
  }
}

@Component({
  selector: 'rms-date-picker',
  templateUrl: './date-picker.component.html',
  standalone: false,
  styleUrl: './date-picker.component.scss',
  providers: [
    { provide: DateAdapter, useClass: MultiFormatLuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatePickerComponent)
    }
  ],
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {

  // Source of the per-instance range input ids below
  private static nextId = 0;

  @Input() isRange = false;
  @Input() placeholder = 'M/D/Y';
  @Input() min: string | DateTime;
  @Input() max: string | DateTime;
  @Input() height: number; // Height in px
  @Input() required: any = false; // Support for required validation
  @Input() backgroundColor: string;

  // Opt-in: collapse a settled range into a summary pill laid over the inputs (RA-2738).
  @Input() showPill = false;

  /**
   * The pill's ✕. Clearing means different things to different consumers (one re-queries the
   * server, another also drops a sort scope), so the picker resets itself and lets the consumer
   * decide what else to do rather than emitting an empty range through `onChange`.
   */
  @Output() pillClear = new EventEmitter<void>();

  // Material's own range inputs, needed to reset text the parser rejected (see clearRangeInputText)
  @ViewChild(MatStartDate) private readonly startDateInput: MatStartDate<DateTime>;
  @ViewChild(MatEndDate) private readonly endDateInput: MatEndDate<DateTime>;

  // The raw <input> elements, needed to read text the parser rejected and to place the caret
  @ViewChild('startInput', { read: ElementRef }) private readonly startInputEl: ElementRef<HTMLInputElement>;
  @ViewChild('endInput', { read: ElementRef }) private readonly endInputEl: ElementRef<HTMLInputElement>;

  // Per-instance ids tying each range input to its hidden label; a page often holds several pickers
  public readonly startInputId = `date-range-start-${DatePickerComponent.nextId}`;
  public readonly endInputId = `date-range-end-${DatePickerComponent.nextId++}`;

  // Internal model for single date
  public dateSelect: DateTime;

  // Internal models for date range
  public dateRangeStart: DateTime;
  public dateRangeEnd: DateTime;

  // Store previous valid values for validation
  private previousDateSelect: DateTime;
  private previousDateRangeStart: DateTime;
  private previousDateRangeEnd: DateTime;

  // Control state
  public disabled = false;

  // True when either half holds text the parser rejected, or when the range runs backwards
  public rangeInvalid = false;

  // Which half the user needs to correct, so the pill can drop the caret in the right input
  public invalidHalf: 'start' | 'end' = null;

  // True while the user has one of the range inputs focused, which stands the pill down
  public pillEditing = false;

  // The last pair handed to the consumer, so an unchanged range does not re-trigger their search
  private appliedStart: DateTime = null;
  private appliedEnd: DateTime = null;

  // ControlValueAccessor callbacks
  protected onChange: (value: DateTime | DateRange) => void = () => {};
  protected onTouched: () => void = () => {};

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngOnInit() {
    if (this.min && typeof this.min === 'string') {
      this.min = DateTime.fromFormat(this.min, CONFIG.dateTimeServerFormat);
    }
    if (this.max && typeof this.max === 'string') {
      this.max = DateTime.fromFormat(this.max, CONFIG.dateTimeServerFormat);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: DateTime | DateRange | null): void {
    if (this.isRange) {
      // Handle date range
      if (value && typeof value === 'object' && 'startDate' in value) {
        const dateRange = value as DateRange;
        this.dateRangeStart = dateRange.startDate;
        this.dateRangeEnd = dateRange.endDate;
      } else {
        this.dateRangeStart = null;
        this.dateRangeEnd = null;
      }
      this.previousDateRangeStart = this.dateRangeStart;
      this.previousDateRangeEnd = this.dateRangeEnd;
      this.appliedStart = this.dateRangeStart || null;
      this.appliedEnd = this.dateRangeEnd || null;
      this.rangeInvalid = false;
      this.invalidHalf = null;
      this.clearRangeInputText();
    } else {
      // Handle single date
      if (value && value instanceof DateTime) {
        this.dateSelect = value;
        this.previousDateSelect = value;
      } else if (value && typeof value === 'string') {
        // Handle string dates if needed
        this.dateSelect = DateTime.fromISO(value);
        this.previousDateSelect = this.dateSelect;
      } else {
        this.dateSelect = null;
        this.previousDateSelect = null;
      }
    }
  }

  registerOnChange(fn: (value: DateTime | DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** True when the pill is standing in for the inputs. */
  public get pillVisible(): boolean {
    if (!this.isRange || !this.showPill || this.pillEditing || this.disabled) {
      return false;
    }

    return this.rangeInvalid || (!!this.dateRangeStart && !!this.dateRangeEnd);
  }

  /**
   * The pill's text. A half the parser understood reads as a formatted date; one it did not is
   * echoed back exactly as typed, so the user can see which characters need fixing (RA-2738).
   */
  public get pillLabel(): string {
    const startLabel = this.dateRangeStart ? this.dayLabel(this.dateRangeStart) : this.startText;
    const endLabel = this.dateRangeEnd ? this.dayLabel(this.dateRangeEnd) : this.endText;

    if (startLabel && endLabel) {
      return startLabel === endLabel ? startLabel : `${startLabel} \u2013 ${endLabel}`;
    }

    return startLabel || endLabel;
  }

  /** True when either half holds anything at all, parsed or not, so the ✕ can offer to clear it. */
  public get hasRangeText(): boolean {
    return !!this.dateRangeStart || !!this.dateRangeEnd || !!this.startText || !!this.endText;
  }

  // Date change handlers
  public onDateChanged(): void {
    // Emit null if date is cleared, otherwise emit the DateTime
    const value = this.dateSelect || null;
    this.onChange(value);
  }

  public onDateBlur(): void {
    this.onTouched();

    // Validate against min/max constraints
    if (this.dateSelect) {
      if (this.min && this.dateSelect < (this.min as DateTime)) {
        this.dateSelect = this.previousDateSelect;
        this.onChange(this.previousDateSelect);
      } else if (this.max && this.dateSelect > (this.max as DateTime)) {
        this.dateSelect = this.previousDateSelect;
        this.onChange(this.previousDateSelect);
      } else {
        // Valid date, update previous value
        this.previousDateSelect = this.dateSelect;
      }
    } else {
      // Date was cleared, update previous value
      this.previousDateSelect = null;
    }
  }

  public onRangeFocus(): void {
    this.pillEditing = true;
  }

  /**
   * Settles the range once the user is done with it. Nothing is published while they type: a
   * part-typed year reads as a real date (2026 passes through 202, i.e. 0202) and every keystroke
   * used to filter the consumer's data against it (RA-2738).
   */
  public onDateRangeBlur(event?: FocusEvent): void {
    this.onTouched();

    // A move to the other half, or to the calendar toggle, is still part of entering one range
    if (this.focusRemainsInControl(event)) {
      return;
    }

    this.pillEditing = false;
    this.settleRange();
  }

  /** The calendar hands back a complete, ordered range, so publish it as soon as it closes. */
  public onPickerClosed(): void {
    if (!this.isRange) {
      return;
    }

    this.pillEditing = false;
    this.settleRange();
  }

  public clearDateRange(): void {
    this.resetRange();
    this.commitRange();
  }

  /** Hands the ✕ back to the consumer; see the `pillClear` output. */
  public onPillClear(): void {
    this.resetRange();
    this.pillClear.emit();
  }

  /** Reopens the inputs for editing, with the caret on whichever half needs correcting. */
  public onPillEdit(): void {
    this.pillEditing = true;

    const target = this.invalidHalf === 'end' ? this.endInputEl : this.startInputEl;
    const input = target?.nativeElement;

    if (!input) {
      return;
    }

    input.focus();

    if (this.rangeInvalid) {
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }

  /**
   * Applies the min/max limits, decides whether what is left can be used, and publishes it. An
   * unusable range stays on screen as a red pill instead of being pushed to the consumer, so a
   * typo never silently empties their results.
   */
  private settleRange(): void {
    this.applyRangeLimits();
    this.evaluateRange();

    if (!this.rangeInvalid) {
      this.previousDateRangeStart = this.dateRangeStart;
      this.previousDateRangeEnd = this.dateRangeEnd;
      this.commitRange();
    }
  }

  /**
   * Restores the last good value for any half that fell outside the min/max limits. Only a half
   * that was reverted gets its text rewritten — blanking the rest would wipe the unparsed text
   * the red pill has to echo back.
   */
  private applyRangeLimits(): void {
    if (this.dateRangeStart && this.isOutsideLimits(this.dateRangeStart)) {
      this.dateRangeStart = this.previousDateRangeStart;

      if (this.startDateInput && !this.dateRangeStart) {
        this.startDateInput.value = null;
      }
    }

    if (this.dateRangeEnd && this.isOutsideLimits(this.dateRangeEnd)) {
      this.dateRangeEnd = this.previousDateRangeEnd;

      if (this.endDateInput && !this.dateRangeEnd) {
        this.endDateInput.value = null;
      }
    }
  }

  private isOutsideLimits(date: DateTime): boolean {
    return (!!this.min && date < (this.min as DateTime)) || (!!this.max && date > (this.max as DateTime));
  }

  /** Flags text the parser rejected and ranges that run backwards, and notes which half to fix. */
  private evaluateRange(): void {
    const startUnreadable = !this.dateRangeStart && !!this.startText;
    const endUnreadable = !this.dateRangeEnd && !!this.endText;
    const backwards = !!this.dateRangeStart && !!this.dateRangeEnd && this.dateRangeEnd < this.dateRangeStart;

    this.rangeInvalid = startUnreadable || endUnreadable || backwards;

    if (!this.rangeInvalid) {
      this.invalidHalf = null;
    } else {
      this.invalidHalf = startUnreadable ? 'start' : 'end';
    }
  }

  /** Publishes the range, unless the consumer is already holding exactly this pair. */
  private commitRange(): void {
    const startDate = this.dateRangeStart || null;
    const endDate = this.dateRangeEnd || null;

    if (this.isSameDate(startDate, this.appliedStart) && this.isSameDate(endDate, this.appliedEnd)) {
      return;
    }

    this.appliedStart = startDate;
    this.appliedEnd = endDate;
    this.onChange({ startDate, endDate });
  }

  private isSameDate(left: DateTime, right: DateTime): boolean {
    if (!left || !right) {
      return !left && !right;
    }

    return left.valueOf() === right.valueOf();
  }

  private resetRange(): void {
    this.dateRangeStart = null;
    this.dateRangeEnd = null;
    this.previousDateRangeStart = null;
    this.previousDateRangeEnd = null;
    this.rangeInvalid = false;
    this.invalidHalf = null;
    this.clearRangeInputText();
  }

  /** One end of the pill label. The year shows only when the date falls outside the current one. */
  private dayLabel(date: DateTime): string {
    const currentYear = DateTime.now().setZone(CONFIG.formatEST).year;

    return date.year === currentYear ? date.toFormat('MMM d') : date.toFormat('MMM d, yyyy');
  }

  private get startText(): string {
    return this.startInputEl?.nativeElement?.value.trim() ?? '';
  }

  private get endText(): string {
    return this.endInputEl?.nativeElement?.value.trim() ?? '';
  }

  /**
   * True when the blur handed focus to another part of this control, such as the second input or
   * the calendar toggle. Those moves are still part of entering one range, so nothing is emitted.
   */
  private focusRemainsInControl(event?: FocusEvent): boolean {
    const nextTarget = event?.relatedTarget;

    return nextTarget instanceof Node && this.elementRef.nativeElement.contains(nextTarget);
  }

  /**
   * Blanks the text of whichever range input no longer holds a date. Material only rewrites an
   * input when its model value changes, so text it failed to parse (which already left the model
   * null) survives any later reset to null — the leftover the ✕ button and the consumer's own
   * clear both used to show (RA-2738).
   */
  private clearRangeInputText(): void {
    if (this.startDateInput && !this.dateRangeStart) {
      this.startDateInput.value = null;
    }

    if (this.endDateInput && !this.dateRangeEnd) {
      this.endDateInput.value = null;
    }
  }
}

export interface DateRange {
  startDate: DateTime;
  endDate: DateTime;
}
